import { captureException } from '@sentry/browser'
import { UploadHttpError, UploadNetworkError } from '../utils/upload-file'
import type { UploadFileOptions } from '../utils/upload-file'
import { uploadFileWithRetry } from '../utils/upload-file-with-retry'
import { sendStudioEvent } from './amplitude'
import type { StudioTool } from './amplitude'

export type UploadFailure =
  | { reason: 'http'; httpStatus: number }
  | { reason: 'network' | 'aborted' | 'unknown' }

export interface UploadFileWithTelemetryOptions extends UploadFileOptions {
  telemetryTool: StudioTool
  /** The generation, session or asset the upload belongs to. Omit when the upload stands alone. */
  sessionId?: string
  eventProperties?: Record<string, unknown>
}

export function describeUploadFailure(error: unknown): UploadFailure {
  if (error instanceof UploadHttpError) {
    return { reason: 'http', httpStatus: error.status }
  }
  if (error instanceof UploadNetworkError) {
    return { reason: 'network' }
  }
  if (error instanceof DOMException && error.name === 'AbortError') {
    return { reason: 'aborted' }
  }
  return { reason: 'unknown' }
}

/** Failures `uploadFileWithTelemetry` already reported, so a caller's own tracking can skip them. */
export function isReportedUploadFailure(error: unknown): boolean {
  return describeUploadFailure(error).reason !== 'unknown'
}

function elapsedMs(startedAt: number): number {
  return Math.round(performance.now() - startedAt)
}

/**
 * The one way studio code uploads to a presigned URL: `uploadFileWithRetry`, reported as
 * `<Tool> - Upload Started / Completed / Failed` to Amplitude and failures to Sentry, so one
 * dashboard covers every tool. `attempts` on the completed and failed events shows how many
 * uploads a retry saved.
 *
 * For a new tool: add it to `STUDIO_TOOLS`, pass it as `telemetryTool` together with the
 * generation, session or asset id as `sessionId`, and wrap any tool-specific `Upload Failed`
 * tracking in `isReportedUploadFailure` so transport failures are not counted twice.
 *
 * Not a hook, and reports from the upload rather than from mutation callbacks: callers close
 * dialogs and navigate mid-upload, tearing down React observers while the transfer runs on.
 *
 * Rethrows the same error object it captured, so a caller that also captures it in a generic
 * `onError` does not produce a second Sentry event: the SDK skips exceptions it has already seen.
 */
export async function uploadFileWithTelemetry({
  telemetryTool,
  sessionId,
  eventProperties,
  ...uploadOptions
}: UploadFileWithTelemetryOptions): Promise<void> {
  const { file, onProgress } = uploadOptions
  const uploadProperties: Record<string, unknown> = {
    sessionId,
    fileName: file.name,
    fileSize: file.size,
    fileType: file.type,
    ...eventProperties,
  }
  const startedAt = performance.now()
  let attempts = 1
  let lastProgressPercent = 0

  sendStudioEvent(`${telemetryTool} - Upload Started`, uploadProperties)

  try {
    await uploadFileWithRetry({
      ...uploadOptions,
      onRetry: attempt => {
        attempts = attempt
      },
      onProgress: (progress, progressFile) => {
        lastProgressPercent = progress
        onProgress?.(progress, progressFile)
      },
    })
  } catch (error) {
    const failure = describeUploadFailure(error)
    const failureProperties = {
      ...uploadProperties,
      ...failure,
      attempts,
      progressPercent: lastProgressPercent,
      durationMs: elapsedMs(startedAt),
    }

    sendStudioEvent(`${telemetryTool} - Upload Failed`, failureProperties)

    if (failure.reason !== 'aborted') {
      captureException(error, {
        tags: { uploadTool: telemetryTool, uploadFailureReason: failure.reason },
        extra: failureProperties,
      })
    }

    throw error
  }

  sendStudioEvent(`${telemetryTool} - Upload Completed`, {
    ...uploadProperties,
    attempts,
    durationMs: elapsedMs(startedAt),
  })
}
